import { useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { allAiNoticePointsAccepted } from "@/lib/scenarios/aiNotice";

/** Consent points shown before a live avatar session when the trainer enables the AI notice. */
export const AI_DISCLAIMER_POINTS = [
  "I understand I'm interacting with an AI-generated avatar, and my voice and text are processed to generate responses.",
  "I acknowledge that AI responses can be inaccurate or incomplete and aren't professional advice.",
  "I agree not to share personal, sensitive, or confidential information during this session.",
  "I understand that my conversation is transcribed (not recorded) and the transcript may be reviewed by my trainer.",
];

/** Gates the session: renders children only after the learner ticks every point and agrees. */
export function AiConsentGate({ children }: { children: React.ReactNode }) {
  const [agreed, setAgreed] = useState(false);
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState<boolean[]>(AI_DISCLAIMER_POINTS.map(() => false));
  const [reviewOpen, setReviewOpen] = useState(false);
  const canAgree = allAiNoticePointsAccepted(checked);

  if (agreed) {
    return (
      <>
        {children}
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          className="fixed top-2 left-2 z-50 flex items-center gap-1 rounded-full border border-border bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur hover:bg-background"
          aria-label="Show AI notice"
        >
          <Info className="h-3.5 w-3.5" /> AI notice
        </button>
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>AI notice</DialogTitle>
              <DialogDescription>You agreed to the following before this session.</DialogDescription>
            </DialogHeader>
            <ul className="list-disc space-y-2 pl-5 text-sm text-foreground">
              {AI_DISCLAIMER_POINTS.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      {!open && (
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Please agree to the AI notice to start your session.</p>
          <Button onClick={() => setOpen(true)}>Review & start</Button>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl p-8 backdrop-blur-sm">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-3xl font-bold tracking-tight">Before we begin...</DialogTitle>
            <DialogDescription className="text-base">
              Please review and agree to the following to start your interactive session.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {AI_DISCLAIMER_POINTS.map((p, i) => (
              <label key={p} className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-foreground">
                <Checkbox
                  className="mt-0.5 h-5 w-5"
                  checked={checked[i]}
                  onCheckedChange={(v) => setChecked((c) => c.map((x, j) => (j === i ? v === true : x)))}
                />
                <span>{p}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!canAgree} onClick={() => setAgreed(true)} className="rounded-full px-6">
              I agree & start session
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
